// Chép nguyên sang HP-Media/src/app/features/gala/lib/seat-layout.ts — sửa cả hai nơi.
//
// Xếp sơ đồ phòng tiệc: bàn tròn / bàn dài theo lưới, ghế quanh bàn. Hàm THUẦN (không React, không
// thư viện, không import từ dự án) để cùng một file chạy ở trang quản trị và trong app.
// Toạ độ theo "đơn vị thiết kế" (px); nơi vẽ tự co theo khung bằng SVG viewBox.
// Luật và số đo: docs/gala/04-dac-ta-cho-ngoi-2026-10-13.md mục 5.

export const CELL = 220;
export const STAGE_H = 44;
export const STAGE_GAP = 28;
export const PAD = 20;
export const TABLE_D = 96;
export const SEAT_R = 13;
export const LONG_W = 150;
export const LONG_H = 56;

/** Ghế cách mép bàn 8px. */
const SEAT_GAP = 8;
/** Nhãn phụ ("3/10") nằm dưới mép dưới của hàng ghế 14px. */
const LABEL_GAP = 14;

export type LayoutTableIn = {
  id: number;
  name: string;
  shape: 'ROUND' | 'LONG';
  capacity: number;
  sortOrder: number;
};

export type SeatPos = { number: number; x: number; y: number };

export type TableLayout = {
  id: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
  shape: 'ROUND' | 'LONG';
  /** Chỗ đặt nhãn phụ dưới bàn (đã tính cả vòng/hàng ghế). */
  labelY: number;
  /** Bán kính vòng ghế của bàn này (7..13): bàn đông ghế thì ghế nhỏ lại để không chồng nhau. */
  seatR: number;
  seats: SeatPos[];
};

export type RoomLayout = {
  width: number;
  height: number;
  stage: { x: number; y: number; w: number; h: number };
  tables: TableLayout[];
};

/** Ghế nhỏ nhất vẫn đọc được số. */
const SEAT_R_MIN = 7;
/** Bàn dài quá 7 ghế mỗi hàng: hàng ghế trải rộng hơn mặt bàn, chừa 4px mỗi bên trong ô lưới. */
const LONG_ROW_MAX = 7;
const LONG_WIDE_SPAN = CELL - 2 * SEAT_R - 8;

const clampSeatR = (r: number) => Math.min(SEAT_R, Math.max(SEAT_R_MIN, r));

/** Vòng ghế bàn tròn: ghế chia đều chu vi bán kính 69, mỗi ghế chiếm một cung, chừa 1px hở. */
function roundSeatR(n: number): number {
  const ring = TABLE_D / 2 + SEAT_R + SEAT_GAP;
  return clampSeatR(Math.floor((2 * Math.PI * ring) / n / 2) - 1);
}

/** Đoạn ngang (tâm ghế đầu → tâm ghế cuối) của một hàng ghế bàn dài có `top` ghế ở hàng trên. */
function longSpan(top: number): number {
  return top <= LONG_ROW_MAX ? LONG_W - 2 * SEAT_R : LONG_WIDE_SPAN;
}

function longSeatR(n: number): number {
  const top = Math.ceil(n / 2);
  if (top === 1) return SEAT_R;
  return clampSeatR(Math.floor(longSpan(top) / (top - 1) / 2) - 1);
}

function roundSeats(cx: number, cy: number, n: number): SeatPos[] {
  const radius = TABLE_D / 2 + SEAT_R + SEAT_GAP;
  const seats: SeatPos[] = [];
  for (let i = 0; i < n; i++) {
    // Ghế 1 thẳng phía trên tâm, tăng dần theo chiều kim đồng hồ (y hướng xuống màn hình).
    const angle = ((-90 + (i * 360) / n) * Math.PI) / 180;
    seats.push({ number: i + 1, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
  }
  return seats;
}

function longSeats(cx: number, cy: number, n: number): SeatPos[] {
  const top = Math.ceil(n / 2);
  const bottom = n - top;
  const span = longSpan(top);
  const left = cx - span / 2;
  const right = cx + span / 2;
  const rowX = (index: number, count: number) =>
    count === 1 ? cx : left + (index * (right - left)) / (count - 1);
  const offset = LONG_H / 2 + SEAT_R + SEAT_GAP;
  const seats: SeatPos[] = [];
  for (let i = 0; i < top; i++) seats.push({ number: i + 1, x: rowX(i, top), y: cy - offset });
  for (let i = 0; i < bottom; i++) {
    seats.push({ number: top + i + 1, x: rowX(i, bottom), y: cy + offset });
  }
  return seats;
}

/** Bàn xếp theo sortOrder tăng dần rồi id; bàn thứ k vào ô (k mod cột, ⌊k / cột⌋). */
export function layoutRoom(tables: LayoutTableIn[], opts: { columns: 2 | 3 | 4 }): RoomLayout {
  const { columns } = opts;
  const sorted = [...tables].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  const rows = Math.ceil(sorted.length / columns);
  const gridTop = PAD + STAGE_H + STAGE_GAP;

  const laid = sorted.map((t, k): TableLayout => {
    const cx = PAD + (k % columns) * CELL + CELL / 2;
    const cy = gridTop + Math.floor(k / columns) * CELL + CELL / 2;
    if (t.shape === 'LONG') {
      return {
        id: t.id,
        cx,
        cy,
        w: LONG_W,
        h: LONG_H,
        shape: 'LONG',
        labelY: cy + LONG_H / 2 + SEAT_GAP + SEAT_R * 2 + LABEL_GAP,
        seatR: longSeatR(t.capacity),
        seats: longSeats(cx, cy, t.capacity),
      };
    }
    return {
      id: t.id,
      cx,
      cy,
      w: TABLE_D,
      h: TABLE_D,
      shape: 'ROUND',
      labelY: cy + TABLE_D / 2 + SEAT_GAP + SEAT_R * 2 + LABEL_GAP,
      seatR: roundSeatR(t.capacity),
      seats: roundSeats(cx, cy, t.capacity),
    };
  });

  return {
    width: PAD * 2 + columns * CELL,
    height: PAD * 2 + STAGE_H + STAGE_GAP + rows * CELL,
    stage: { x: PAD, y: PAD, w: columns * CELL, h: STAGE_H },
    tables: laid,
  };
}

/** Số cột lưới theo bề rộng khung (px): ≥ 900 → 4, ≥ 680 → 3, còn lại 2. */
export function columnsForWidth(px: number): 2 | 3 | 4 {
  if (px >= 900) return 4;
  if (px >= 680) return 3;
  return 2;
}
